import {useCallback, useEffect, useRef, useState} from "react";
import {discardTemporaryImage, uploadTemporaryImage} from "@/api/ImageApi.ts";
import {Image} from "@/images/Image.ts";

export function useTemporaryImageUpload() {
    const [uploading, setUploading] = useState(false);
    const pendingImage = useRef<Image | undefined>(undefined);
    const controller = useRef<AbortController | undefined>(undefined);
    const sequence = useRef(0);

    useEffect(() => () => {
        controller.current?.abort();
        if (pendingImage.current) void discardTemporaryImage(pendingImage.current).catch(() => undefined);
    }, []);

    const upload = useCallback(async (file: File): Promise<Image | undefined> => {
        const currentSequence = ++sequence.current;
        controller.current?.abort();
        const nextController = new AbortController();
        controller.current = nextController;
        setUploading(true);
        try {
            const image = await uploadTemporaryImage(file, nextController.signal);
            if (currentSequence !== sequence.current) {
                await discardTemporaryImage(image);
                return undefined;
            }

            const previousImage = pendingImage.current;
            pendingImage.current = image;
            if (previousImage) void discardTemporaryImage(previousImage).catch(() => undefined);
            return image;
        } catch (reason) {
            if (nextController.signal.aborted) return undefined;
            throw reason;
        } finally {
            if (currentSequence === sequence.current) setUploading(false);
        }
    }, []);

    const commit = useCallback(() => {
        pendingImage.current = undefined;
    }, []);

    const clear = useCallback(() => {
        sequence.current++;
        controller.current?.abort();
        const image = pendingImage.current;
        pendingImage.current = undefined;
        if (image) void discardTemporaryImage(image).catch(() => undefined);
        setUploading(false);
    }, []);

    return {upload, commit, clear, uploading};
}
